/**
 * Shared UI component for UI Autocomplete Field.
 * PARENT-CHILD CONTRACT: parent screens provide the data and configuration that
 * this wrapper renders or normalizes for reuse across the application.
 */
import { ChangeDetectionStrategy, Component, computed, inject, input, output, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { AutoCompleteModule, AutoCompleteSelectEvent } from 'primeng/autocomplete';

import { UiAutocompleteOption } from './ui-autocomplete-field.types';
import { UiLabelTranslatePipe } from '../ui-label-translate.pipe';
import { resolveTranslatedUiLabel } from '../ui-label-translate.util';

interface UiPrimeAutocompleteOption extends UiAutocompleteOption {
  translatedCaption: string;
  translatedLabel: string;
}

/**
 * COMPONENT: Ui Autocomplete Field
 * Inputs: Uses the parameters, injected services, route state, or signal state declared by this file.
 * Output: Returns the documented value or updates component/service state without changing the public contract.
 * Processing: Documents the Angular declaration that connects this class to templates, dependency injection, or template transforms.
 */
@Component({
  selector: 'ui-autocomplete-field',
  standalone: true,
  imports: [AutoCompleteModule, FormsModule, UiLabelTranslatePipe],
  templateUrl: './ui-autocomplete-field.component.html',
  styleUrl: './ui-autocomplete-field.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class UiAutocompleteFieldComponent {
  private readonly translate = inject(TranslateService);
  /** STATE OWNER: languageVersion documents the signal state boundary used by this file. */
  private readonly languageVersion = signal(0);

  /** PARENT-CHILD CONTRACT: label documents the input state boundary used by this file. */
  readonly label = input('');
  /** PARENT-CHILD CONTRACT: value documents the input state boundary used by this file. */
  readonly value = input('');
  /** PARENT-CHILD CONTRACT: placeholder documents the input state boundary used by this file. */
  readonly placeholder = input('');
  /** PARENT-CHILD CONTRACT: required documents the input state boundary used by this file. */
  readonly required = input(false);
  /** PARENT-CHILD CONTRACT: readonly documents the input state boundary used by this file. */
  readonly readonly = input(false);
  /** PARENT-CHILD CONTRACT: disabled documents the input state boundary used by this file. */
  readonly disabled = input(false);
  /** PARENT-CHILD CONTRACT: loading documents the input state boundary used by this file. */
  readonly loading = input(false);
  /** PARENT-CHILD CONTRACT: error documents the input state boundary used by this file. */
  readonly error = input('');
  /** PARENT-CHILD CONTRACT: hint documents the input state boundary used by this file. */
  readonly hint = input('');
  /** PARENT-CHILD CONTRACT: emptyMessage documents the input state boundary used by this file. */
  readonly emptyMessage = input('No matches found.');
  /** PARENT-CHILD CONTRACT: minSearchLength documents the input state boundary used by this file. */
  readonly minSearchLength = input(3);
  /** PARENT-CHILD CONTRACT: showEmptyState documents the input state boundary used by this file. */
  readonly showEmptyState = input(true);
  readonly options = input<UiAutocompleteOption[]>([]);
  readonly valueChange = output<string>();
  readonly optionSelected = output<string>();
  readonly resolvedOptions = computed<UiPrimeAutocompleteOption[]>(() => {
    this.languageVersion();

    return this.options().map((option) => ({
      ...option,
      translatedCaption: option.caption ? this.resolveLabel(option.caption) : '',
      translatedLabel: this.resolveLabel(option.label)
    }));
  });

  /**
   * BUSINESS LOGIC: constructor
   * Inputs: Uses the parameters, injected services, route state, or signal state declared by this file.
   * Output: Returns the documented value or updates component/service state without changing the public contract.
   * Processing: Coordinates validation, mapping, emitted events, API calls, or state updates already owned by this implementation.
   */
  constructor() {
    this.translate.onLangChange.pipe(takeUntilDestroyed()).subscribe(() => {
      this.languageVersion.update((version) => version + 1);
    });
  }

  /**
   * BUSINESS LOGIC: onValueChange
   * Inputs: Uses the parameters, injected services, route state, or signal state declared by this file.
   * Output: Returns the documented value or updates component/service state without changing the public contract.
   * Processing: Coordinates validation, mapping, emitted events, API calls, or state updates already owned by this implementation.
   */
  onValueChange(value: string | UiPrimeAutocompleteOption | null | undefined): void {
    if (typeof value === 'string') {
      this.valueChange.emit(value);
    }
  }

  /**
   * BUSINESS LOGIC: onSelect
   * Inputs: Uses the parameters, injected services, route state, or signal state declared by this file.
   * Output: Returns the documented value or updates component/service state without changing the public contract.
   * Processing: Coordinates validation, mapping, emitted events, API calls, or state updates already owned by this implementation.
   */
  onSelect(event: AutoCompleteSelectEvent): void {
    const option = event.value as UiPrimeAutocompleteOption | undefined;

    if (option?.value) {
      this.optionSelected.emit(option.value);
    }
  }

  /**
   * BUSINESS LOGIC: resolveLabel
   * Inputs: Uses the parameters, injected services, route state, or signal state declared by this file.
   * Output: Returns the documented value or updates component/service state without changing the public contract.
   * Processing: Coordinates validation, mapping, emitted events, API calls, or state updates already owned by this implementation.
   */
  private resolveLabel(value: string): string {
    return resolveTranslatedUiLabel(this.translate, value);
  }
}
